import { _ as _defineProperty } from '../defineProperty-Y-S4SptN.js';
import { _ as _slicedToArray } from '../slicedToArray-ibbgR3-O.js';
import { _ as _objectWithoutProperties } from '../objectWithoutProperties-w3bsUgPa.js';
import { forwardRef, useState } from 'react';
import classNames from 'classnames';
import noop from '../noop/index.js';
import { I as InputSize, a as InputVariant } from '../types-arAyTUq_.js';
import { FontWeights } from '../fonts/index.js';
import createUseStyles from '../create/index.js';
import { inputTypeNumberWithoutArrows } from '../helpers/index.js';
import { j as jsxRuntimeExports } from '../jsx-runtime-KJzvSKZ_.js';

function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var paddings = {
  28: '4px 12px',
  32: '6px 12px',
  40: '10px 12px',
  58: '14px 12px'
};
var useStyles = createUseStyles({
  root: {
    position: 'relative',
    height: function height(props) {
      return props.size;
    },
    display: 'flex',
    borderRadius: 12,
    alignItems: 'center',
    padding: function padding(props) {
      return paddings[props.size];
    }
    // width: '100%',
  },
  filled: {
    background: 'var(--backgrounds-tint-2)'
  },
  outline: {
    border: '1px solid var(--backgrounds-tint-4)'
  },
  largInput: {
    display: 'flex',
    alignItems: 'center'
  },
  input: _objectSpread$1({
    color: 'var(--texts-base)',
    height: '100%',
    width: '100%',
    outline: 'none',
    border: 'none',
    fontWeight: FontWeights.Medium,
    background: 'transparent',
    fontSize: 14,
    transition: 'all 100ms ease-out'
  }, inputTypeNumberWithoutArrows),
  inputFocused: {
    transform: 'translatey(15%)'
  },
  labelText: {
    transition: 'all 200ms ease-out',
    position: 'absolute',
    color: 'var(--texts-tint-1)',
    top: '50%',
    left: 12,
    transform: 'translatey(-50%)',
    fontSize: 14
  },
  disabled: {
    opacity: 'var(--statuses-disabled)'
    // borderColor: 'var(--x-text-color-disabled)',
  },
  focused: {
    transform: 'translate(-12%, -120%) scale(0.7)',
    color: 'var(--accents-primary-base)'
  },
  hasValue: {
    color: 'var(--texts-tint-1)'
  },
  icon: {
    width: 20,
    height: 20,
    display: 'inline-block',
    '& svg': {
      width: 20,
      height: 20
    },
    '& path': {
      fill: 'var(--texts-tint-1)'
    }
  },
  iconFocused: {
    '& path': {
      fill: 'var(--texts-base)'
    }
  },
  disabledIcon: {
    '& path': {
      fill: 'var(--x-text-color-disabled)'
    }
  },
  withLeftIcon: {
    left: 46
  },
  leftIcon: {
    marginRight: 12,
    pointerEvents: 'none'
  },
  bigIcon: {
    width: 24,
    height: 24,
    '& svg': {
      width: 24,
      height: 24
    }
  },
  rightIcon: {},
  invalid: {
    color: 'var(--statuses-error-base)'
  },
  errorMessage: {
    fontSize: 10,
    paddingInlineStart: 12
  }
});

var _excluded = ["label", "id", "value", "placeholder", "leftIcon", "rightIcon", "size", "onBlur", "onFocus", "onChange", "defaultValue", "invalid", "errorMessage", "disabled", "variant", "ariaLabel", "className", "rootClassName", "type", "min", "max"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function Input(_ref, ref) {
  var label = _ref.label,
    id = _ref.id,
    value = _ref.value,
    placeholder = _ref.placeholder,
    leftIcon = _ref.leftIcon,
    rightIcon = _ref.rightIcon,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? InputSize.Md : _ref$size,
    _ref$onBlur = _ref.onBlur,
    onBlur = _ref$onBlur === void 0 ? noop : _ref$onBlur,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === void 0 ? noop : _ref$onFocus,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? noop : _ref$onChange,
    _ref$defaultValue = _ref.defaultValue,
    defaultValue = _ref$defaultValue === void 0 ? '' : _ref$defaultValue,
    invalid = _ref.invalid,
    errorMessage = _ref.errorMessage,
    disabled = _ref.disabled,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? InputVariant.Filled : _ref$variant,
    ariaLabel = _ref.ariaLabel,
    className = _ref.className,
    rootClassName = _ref.rootClassName,
    type = _ref.type,
    _ref$min = _ref.min,
    min = _ref$min === void 0 ? 0 : _ref$min,
    _ref$max = _ref.max,
    max = _ref$max === void 0 ? 100 : _ref$max,
    props = _objectWithoutProperties(_ref, _excluded);
  var classes = useStyles({
    size: size
  });
  var isControlled = typeof value !== 'undefined';
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    focused = _useState2[0],
    setFocused = _useState2[1];
  var _useState3 = useState(defaultValue),
    _useState4 = _slicedToArray(_useState3, 2),
    localValue = _useState4[0],
    setLocalValue = _useState4[1];
  var handleBlur = function handleBlur(e) {
    setFocused(false);
    onBlur(e);
  };
  var handleFocus = function handleFocus(e) {
    setFocused(true);
    onFocus(e);
  };
  var handleChange = function handleChange(event) {
    if (isControlled) {
      onChange(event);
    } else {
      setLocalValue(event.target.value);
    }
  };
  var isLarg = size === InputSize.Xl;
  var currentValue = value !== null && value !== void 0 ? value : localValue;
  return /*#__PURE__*/jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, {
    children: [/*#__PURE__*/jsxRuntimeExports.jsxs("label", {
      className: classNames(classes.root, rootClassName, _defineProperty(_defineProperty(_defineProperty({}, classes.largInput, isLarg), classes.filled, variant === InputVariant.Filled), classes.outline, variant === InputVariant.Outline)),
      children: [leftIcon && /*#__PURE__*/jsxRuntimeExports.jsx("span", {
        className: classNames(classes.leftIcon, classes.icon, _defineProperty(_defineProperty(_defineProperty({}, classes.bigIcon, isLarg), classes.iconFocused, focused || currentValue), classes.disabledIcon, disabled)),
        children: leftIcon
      }), /*#__PURE__*/jsxRuntimeExports.jsx("input", _objectSpread({
        id: id,
        value: value !== null && value !== void 0 ? value : localValue,
        placeholder: !isLarg && placeholder || '',
        className: classNames(classes.input, className, _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, classes.invalid, invalid && !isLarg), classes.disabled, disabled), classes.inputFocused, focused && isLarg), classes.inputFocused, (focused || currentValue) && isLarg)),
        onBlur: handleBlur,
        onFocus: handleFocus,
        onChange: handleChange,
        disabled: disabled,
        ref: ref,
        tabIndex: 0,
        "aria-label": ariaLabel,
        type: type,
        min: min,
        max: max
      }, props)), size === InputSize.Xl && label && /*#__PURE__*/jsxRuntimeExports.jsx("span", {
        className: classNames(classes.labelText, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, classes.withLeftIcon, !!leftIcon), classes.focused, focused || currentValue), classes.hasValue, currentValue && !focused), classes.invalid, invalid), classes.disabled, disabled)),
        children: label
      }), rightIcon && /*#__PURE__*/jsxRuntimeExports.jsx("span", {
        className: classNames(classes.rightIcon, classes.icon, _defineProperty({}, classes.bigIcon, isLarg)),
        children: rightIcon
      })]
    }), errorMessage && invalid && /*#__PURE__*/jsxRuntimeExports.jsx("p", {
      className: classNames(classes.invalid, classes.errorMessage),
      children: errorMessage
    })]
  });
}
var Input$1 = /*#__PURE__*/forwardRef(Input);

export { InputSize, InputVariant, Input$1 as default };
//# sourceMappingURL=index.js.map
